import React, { useState, useEffect } from 'react';
import './VatCalculator.css';
import { Calculation, Currency, VATRate } from './types';

const VatCalculator: React.FC = () => {
    // State for VAT Calculator
    const [activeTab, setActiveTab] = useState<'vat' | 'cost'>('vat');
    const [currency, setCurrency] = useState<Currency>('DKK');
    const [netAmount, setNetAmount] = useState<string>('');
    const [vatRate, setVatRate] = useState<number>(25);

    // State for Cost Calculator
    const [itemName, setItemName] = useState<string>('');
    const [quantity, setQuantity] = useState<string>('1');
    const [unitCost, setUnitCost] = useState<string>('');
    const [additionalCosts, setAdditionalCosts] = useState<string>('0');
    const [includeVAT, setIncludeVAT] = useState<boolean>(false);

    // Results and History
    const [results, setResults] = useState<any>(null);
    const [calculationHistory, setCalculationHistory] = useState<Calculation[]>([]);

    // VAT Rates
    const vatRates: VATRate[] = [
        { value: 25, label: '25% - Standard rate (Denmark)' },
        { value: 20, label: '20% - Many EU countries' },
        { value: 19, label: '19% - Germany standard' },
        { value: 21, label: '21% - Belgium, Netherlands' },
        { value: 22, label: '22% - Italy' },
        { value: 24, label: '24% - Finland' },
        { value: 27, label: '27% - Hungary' },
        { value: 0, label: '0% - Zero rate' }
    ];

    // Exchange rates
    const exchangeRates = {
        'DKK': 1,
        'EUR': 7.45,
        'USD': 6.85,
        'SEK': 0.65
    };

    // Load history from localStorage on mount
    useEffect(() => {
        const savedHistory = localStorage.getItem('nordisk_calc_history');
        if (savedHistory) {
            setCalculationHistory(JSON.parse(savedHistory));
        }
    }, []);

    // Save history to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('nordisk_calc_history', JSON.stringify(calculationHistory));
    }, [calculationHistory]);

    // Calculate VAT
    const calculateVAT = () => {
        const net = parseFloat(netAmount);
        if (!net || net <= 0) {
            alert('Please enter a valid net amount');
            return;
        }

        const vatAmount = net * (vatRate / 100);
        const grossAmount = net + vatAmount;

        // Convert to selected currency
        const conversionRate = exchangeRates[currency];
        const convertedNet = net / conversionRate;
        const convertedVAT = vatAmount / conversionRate;
        const convertedGross = grossAmount / conversionRate;

        // Create calculation object
        const calculation: Calculation = {
            type: 'vat',
            timestamp: new Date().toISOString(),
            netAmount: net,
            vatRate,
            vatAmount,
            grossAmount,
            currency
        };

        // Update history
        const newHistory = [calculation, ...calculationHistory.slice(0, 4)];
        setCalculationHistory(newHistory);

        // Set results
        setResults({
            type: 'vat',
            net: convertedNet,
            vat: convertedVAT,
            gross: convertedGross,
            currency
        });
    };

    // Calculate Cost
    const calculateCost = () => {
        if (!itemName.trim()) {
            alert('Please enter an item name');
            return;
        }

        const qty = parseInt(quantity);
        const unit = parseFloat(unitCost);
        const additional = parseFloat(additionalCosts) || 0;

        if (!unit || unit <= 0 || qty <= 0) {
            alert('Please enter valid quantity and unit cost');
            return;
        }

        let subtotal = (unit * qty) + additional;
        let vatAmount = 0;
        let totalCost = subtotal;

        if (includeVAT) {
            vatAmount = subtotal * 0.25;
            totalCost = subtotal + vatAmount;
        }

        // Create calculation object
        const calculation: Calculation = {
            type: 'cost',
            timestamp: new Date().toISOString(),
            itemName,
            quantity: qty,
            unitCost: unit,
            additionalCosts: additional,
            includeVAT,
            subtotal,
            vatAmount,
            totalCost
        };

        // Update history
        const newHistory = [calculation, ...calculationHistory.slice(0, 4)];
        setCalculationHistory(newHistory);

        // Set results
        setResults({
            type: 'cost',
            itemName,
            quantity: qty,
            subtotal,
            vatAmount,
            totalCost,
            includeVAT
        });
    };

    // Reset calculator
    const resetCalculator = () => {
        setResults(null);
        setNetAmount('');
        setItemName('');
        setUnitCost('');
        setQuantity('1');
        setAdditionalCosts('0');
    };

    // Currency symbols
    const currencySymbols = {
        'DKK': 'kr.',
        'EUR': '€',
        'USD': '$',
        'SEK': 'kr'
    };

    // Format currency
    const formatCurrency = (amount: number, curr: Currency) => {
        const symbol = currencySymbols[curr] || curr;
        return `${symbol} ${amount.toFixed(2)}`;
    };

    return (
        <div className="nordisk-calculator-container">
            <h1 className="nordisk-calculator-title">Nordisk VAT & Cost Calculator</h1>

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`tab-btn ${activeTab === 'vat' ? 'active' : ''}`}
                    onClick={() => setActiveTab('vat')}
                >
                    VAT Calculator
                </button>
                <button
                    className={`tab-btn ${activeTab === 'cost' ? 'active' : ''}`}
                    onClick={() => setActiveTab('cost')}
                >
                    Cost Calculator
                </button>
            </div>

            {/* VAT Calculator Tab */}
            {activeTab === 'vat' && (
                <div className="tab-content active">
                    <div className="currency-selector">
                        {(['DKK', 'EUR', 'USD', 'SEK'] as Currency[]).map((curr) => (
                            <button
                                key={curr}
                                className={`currency-btn ${currency === curr ? 'active' : ''}`}
                                onClick={() => setCurrency(curr)}
                            >
                                {curr}
                            </button>
                        ))}
                    </div>

                    <div className="input-group">
                        <label htmlFor="netAmount">Net Amount (without VAT)</label>
                        <input
                            type="number"
                            id="netAmount"
                            value={netAmount}
                            onChange={(e) => setNetAmount(e.target.value)}
                            placeholder="Enter amount"
                            step="0.01"
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="vatRate">VAT Rate (%)</label>
                        <select
                            id="vatRate"
                            value={vatRate}
                            onChange={(e) => setVatRate(parseFloat(e.target.value))}
                        >
                            {vatRates.map((rate) => (
                                <option key={rate.value} value={rate.value}>
                                    {rate.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button className="calculate-btn" onClick={calculateVAT}>
                        Calculate VAT
                    </button>
                </div>
            )}

            {/* Cost Calculator Tab */}
            {activeTab === 'cost' && (
                <div className="tab-content active">
                    <div className="input-group">
                        <label htmlFor="itemName">Item/Service Name</label>
                        <input
                            type="text"
                            id="itemName"
                            value={itemName}
                            onChange={(e) => setItemName(e.target.value)}
                            placeholder="Enter item or service name"
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="quantity">Quantity</label>
                        <input
                            type="number"
                            id="quantity"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            placeholder="Enter quantity"
                            min="1"
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="unitCost">Unit Cost (DKK)</label>
                        <input
                            type="number"
                            id="unitCost"
                            value={unitCost}
                            onChange={(e) => setUnitCost(e.target.value)}
                            placeholder="Enter cost per unit"
                            step="0.01"
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="additionalCosts">Additional Costs (DKK)</label>
                        <input
                            type="number"
                            id="additionalCosts"
                            value={additionalCosts}
                            onChange={(e) => setAdditionalCosts(e.target.value)}
                            placeholder="Shipping, fees, etc."
                            step="0.01"
                        />
                    </div>

                    <div className="input-group">
                        <label>Include VAT?</label>
                        <select
                            id="includeVAT"
                            value={includeVAT ? 'include' : 'exclude'}
                            onChange={(e) => setIncludeVAT(e.target.value === 'include')}
                        >
                            <option value="exclude">Exclude VAT</option>
                            <option value="include">Include VAT (25%)</option>
                        </select>
                    </div>

                    <button className="calculate-btn" onClick={calculateCost}>
                        Calculate Total Cost
                    </button>
                </div>
            )}

            {/* Results Section */}
            {results && (
                <div className="result-section active">
                    <h3>Calculation Results</h3>
                    <div className="result-grid">
                        {results.type === 'vat' ? (
                            <>
                                <div className="result-item">
                                    <div>Net Amount</div>
                                    <div className="result-value">
                                        {formatCurrency(results.net, results.currency)}
                                    </div>
                                </div>
                                <div className="result-item">
                                    <div>VAT Amount</div>
                                    <div className="result-value">
                                        {formatCurrency(results.vat, results.currency)}
                                    </div>
                                </div>
                                <div className="result-item">
                                    <div>Gross Amount</div>
                                    <div className="result-value">
                                        {formatCurrency(results.gross, results.currency)}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="result-item">
                                    <div>Item</div>
                                    <div className="result-value">{results.itemName}</div>
                                    <div className="result-subtext">Qty: {results.quantity}</div>
                                </div>
                                <div className="result-item">
                                    <div>Subtotal</div>
                                    <div className="result-value">kr. {results.subtotal.toFixed(2)}</div>
                                </div>
                                {results.includeVAT && (
                                    <div className="result-item">
                                        <div>VAT (25%)</div>
                                        <div className="result-value">kr. {results.vatAmount.toFixed(2)}</div>
                                    </div>
                                )}
                                <div className="result-item">
                                    <div>Total Cost</div>
                                    <div className="result-value">kr. {results.totalCost.toFixed(2)}</div>
                                </div>
                            </>
                        )}
                    </div>
                    <button className="reset-btn" onClick={resetCalculator}>
                        New Calculation
                    </button>
                </div>
            )}

            {/* History Section */}
            {calculationHistory.length > 0 && (
                <div className="history-section">
                    <h3>Recent Calculations</h3>
                    <div id="historyList">
                        {calculationHistory.map((calc, index) => (
                            <div key={index} className="history-item">
                                {calc.type === 'vat' ? (
                                    <>
                                        <div className="history-title">VAT Calculation</div>
                                        <div>Net: {calc.netAmount.toFixed(2)} {calc.currency}</div>
                                        <div>VAT: {calc.vatAmount.toFixed(2)} {calc.currency} ({calc.vatRate}%)</div>
                                    </>
                                ) : (
                                    <>
                                        <div className="history-title">{calc.itemName}</div>
                                        <div>Total: kr. {calc.totalCost.toFixed(2)}</div>
                                        <div>Qty: {calc.quantity} × kr. {calc.unitCost.toFixed(2)}</div>
                                    </>
                                )}
                                <div className="history-time">
                                    {new Date(calc.timestamp).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VatCalculator;